import { Box, Flex, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CampaignClass } from '@feathr/blackbox';
import {
  Heading,
  Modal,
  Pill,
  PixelWellWithHighlight,
  SegmentedControl,
  Text,
  Well,
} from '@feathr/components';
import { CampaignIcon } from '@feathr/extender/components/CampaignIcons';

type TCampaignSection = 'cultivate' | 'understand' | 'expand';
type TCampaignCategory = 'marketing' | 'monetization';

interface ICampaignType {
  category: TCampaignCategory[];
  cls: CampaignClass;
  description: string;
  name: string;
  recommended?: boolean;
  section: TCampaignSection;
}

const SECTION_TITLES: Record<TCampaignSection, string> = {
  cultivate: 'Cultivate and Activate',
  understand: 'Understand',
  expand: 'Expand',
};

const SECTION_DESCRIPTIONS: Record<TCampaignSection, string> = {
  cultivate:
    'Nurture your community and drive impact with campaigns designed to engage and convert people you already know.',
  understand: 'Gather insights on the behavior, interests, and preferences of your community.',
  expand:
    'Grow your community with campaigns that help you get in front of new, qualified audiences.',
};

const TYPES: ICampaignType[] = [
  {
    name: 'Pixl Plus',
    cls: CampaignClass.Omni,
    description:
      'Target your first-party audiences (cookies and email addresses) and expand reach with third-party audience targeting through a single unified campaign.',
    category: ['marketing', 'monetization'],
    section: 'cultivate',
    recommended: true,
  },
  {
    name: 'Retargeting',
    cls: CampaignClass.Segment,
    description: 'Retarget specific segments of your existing audience with ads across the web.',
    category: ['marketing', 'monetization'],
    section: 'cultivate',
  },
  {
    name: 'Single Send',
    cls: CampaignClass.PinpointEmail,
    description: 'Send a single, scheduled email message to a targeted segment of your audience.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Auto Send',
    cls: CampaignClass.AutoPinpointEmail,
    description: 'Send an email to people who perform a trigger of your choice.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Smart Send',
    cls: CampaignClass.SmartPinpointEmail,
    description:
      'Send an email to a target segment as a response to a target behavior or at a target send time.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Drip',
    cls: CampaignClass.Drip,
    description:
      'Send a series of scheduled email messages to a targeted segment of your audience over a set period of time.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Meta Retargeting',
    cls: CampaignClass.Facebook,
    description: 'Retarget existing audiences with ads on Facebook and Instagram.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Meta Email Mapping',
    cls: CampaignClass.EmailListFacebook,
    description:
      'Reach a specific audience on Facebook and Instagram by targeting ads to a list of email addresses.',
    category: ['marketing'],
    section: 'cultivate',
  },
  {
    name: 'Email Mapping',
    cls: CampaignClass.EmailList,
    description: 'Match a list of emails to cookies that you can then target with ads.',
    category: ['marketing', 'monetization'],
    section: 'cultivate',
  },
  {
    name: 'Tracked Link',
    cls: CampaignClass.TrackedLink,
    description:
      'Track in Feathr the impact of your other marketing efforts by creating and distributing tracked links.',
    category: ['marketing'],
    section: 'understand',
  },
  {
    name: 'Landing Page',
    cls: CampaignClass.LandingPage,
    description:
      'Create a conversion-optimized landing page to use as a destination for someone interacting with your ads.',
    category: ['marketing'],
    section: 'understand',
  },
  {
    name: 'Conversation',
    cls: CampaignClass.Conversation,
    description:
      'Launch a website survey that helps you learn about your visitors and create hyper-focused segments.',
    category: ['marketing'],
    section: 'understand',
  },
  {
    name: 'Mobile Geofencing',
    cls: CampaignClass.MobileGeoFencing,
    description: 'Target mobile devices at a specific location during a specific time.',
    category: ['marketing', 'monetization'],
    section: 'expand',
  },
  {
    name: 'Historical Geofencing',
    cls: CampaignClass.MobileGeoFenceRetargeting,
    description: 'Target mobile devices that were at a specific location some time in the past.',
    category: ['marketing', 'monetization'],
    section: 'expand',
  },
  {
    name: 'Search Keyword',
    cls: CampaignClass.Search,
    description:
      'Build and target an audience of people who have searched for terms related to what your organization provides.',
    category: ['marketing', 'monetization'],
    section: 'expand',
  },
  {
    name: 'Lookalike',
    cls: CampaignClass.SeedSegment,
    description:
      'Based on one of your existing segments, expand your audience with people who are similar.',
    category: ['marketing', 'monetization'],
    section: 'expand',
  },
  {
    name: 'Affinity',
    cls: CampaignClass.Affinity,
    description:
      'Target an affinity audience based on our library of available industry, demographic, and market-related audiences.',
    category: ['marketing', 'monetization'],
    section: 'expand',
  },
  {
    name: 'Invites',
    cls: CampaignClass.Referral,
    description: 'Distribute your Referral Marketing collateral to your Partners.',
    category: ['marketing'],
    section: 'expand',
  },
  {
    name: 'Google Ad Grants',
    cls: CampaignClass.GoogleAdsSmart,
    description:
      'Attract donors, raise awareness, and recruit volunteers with advertising on Google Search.',
    category: ['marketing'],
    section: 'expand',
  },
];

interface ICampaignTypeModalProps {
  onClose: () => void;
  onSelect: (typeName: string) => void;
  opened: boolean;
}

function CampaignTypeModal({
  onClose,
  onSelect,
  opened,
}: Readonly<ICampaignTypeModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [category, setCategory] = useState<TCampaignCategory>('marketing');

  const visible = TYPES.filter((type) => type.category.includes(category));
  const recommended = visible.find((type) => type.recommended);
  const sectioned: TCampaignSection[] = ['expand', 'cultivate', 'understand'];

  return (
    <Modal
      description={t("Choose the type of campaign you'd like to create.")}
      onClose={onClose}
      opened={opened}
      size={'lg'}
      title={t('Create a campaign')}
    >
      <Stack gap={theme.spacing['4']}>
        <Box>
          <SegmentedControl
            data={[
              { label: t('Marketing'), value: 'marketing' },
              { label: t('Monetization'), value: 'monetization' },
            ]}
            name={'campaign-type-category'}
            onChange={(value): void => setCategory(value as TCampaignCategory)}
            value={category}
          />
        </Box>

        {recommended && (
          <PixelWellWithHighlight
            highlighted={true}
            name={recommended.name}
            onClick={(): void => onSelect(recommended.name)}
          >
            <Box p={'6'} ta={'left'}>
              <Flex align={'center'} gap={theme.spacing['6']}>
                <CampaignIcon cls={recommended.cls} hover={false} size={48} />
                <Stack flex={1} gap={theme.spacing['1']}>
                  <Flex align={'center'} gap={theme.spacing['2']}>
                    <Heading order={4} variant={'h4'}>
                      {recommended.name}
                    </Heading>
                    <Pill theme={'cyan'}>{t('New')}</Pill>
                  </Flex>
                  <Text variant={'smallRegular'}>{recommended.description}</Text>
                </Stack>
              </Flex>
            </Box>
          </PixelWellWithHighlight>
        )}

        {sectioned.map((section) => {
          const items = visible.filter((type) => type.section === section && !type.recommended);
          if (items.length === 0) {
            return null;
          }
          return (
            <Stack gap={theme.spacing['2']} key={section}>
              <Heading order={4} variant={'h4'}>
                {t(SECTION_TITLES[section])}
              </Heading>
              <Text variant={'smallRegular'}>{t(SECTION_DESCRIPTIONS[section])}</Text>
              <SimpleGrid cols={3} spacing={theme.spacing['3']}>
                {items.map((type) => (
                  <Well key={type.name} onClick={(): void => onSelect(type.name)} width={'full'}>
                    <Stack
                      align={'flex-start'}
                      gap={theme.spacing['4']}
                      p={'3'}
                      ta={'left'}
                      w={'100%'}
                    >
                      <CampaignIcon cls={type.cls} hover={false} size={48} />
                      <Stack gap={theme.spacing['2']} w={'100%'}>
                        <Heading order={5} variant={'h5'}>
                          {type.name}
                        </Heading>
                        <Text variant={'smallRegular'}>{type.description}</Text>
                      </Stack>
                    </Stack>
                  </Well>
                ))}
              </SimpleGrid>
            </Stack>
          );
        })}
      </Stack>
    </Modal>
  );
}

export default CampaignTypeModal;
